import React, { useState } from 'react';
import { CustomField, FieldLabel, Fields } from '@measured/puck';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import InputField from '../../../components/common/input-field';
import Toggle from '../../../components/common/toggleButton';
import { Select } from '../../components/form-fields/Select';
import CustomTooltip from '../../../components/common/tooltip';

export type InputFieldHeightSubProps = {
  height: {
    mobile: string;
    tablet: string;
    desktop: string;
  };
  rowGap?: {
    mobile: string;
    tablet: string;
    desktop: string;
  };
  colGap?: {
    mobile: string;
    tablet: string;
    desktop: string;
  };
  layout?: {
    mobile: string;
    tablet: string;
    desktop: string;
  };
  deviceType: 'mobile' | 'tablet' | 'desktop';
};

export interface fieldHeightProps {
  heightField: InputFieldHeightSubProps;
}

interface Inputfield {
  height: {
    label: string;
    type: string;
    value: {
      mobile: string;
      tablet: string;
      desktop: string;
    };
  };
  rowGap?: {
    label: string;
    type: string;
    value: {
      mobile: string;
      tablet: string;
      desktop: string;
    };
  };
  colGap?: {
    label: string;
    type: string;
    value: {
      mobile: string;
      tablet: string;
      desktop: string;
    };
  };
  layout?: {
    label: string;
    type: string;
    value: {
      mobile: string;
      tablet: string;
      desktop: string;
    };
  };
}

export const inputFieldHeightProps = (
  isGap = false,
  isGapFieldEnabled = false,
) => {
  const inputField: Inputfield = {
    height: {
      label: 'Height',
      type: 'input',
      value: {
        mobile: '',
        tablet: '',
        desktop: '',
      },
    },
    ...(!isGap && {
      rowGap: {
        label: 'Row Gap',
        type: 'input',
        value: {
          mobile: '10',
          tablet: '10',
          desktop: '10',
        },
      },
      colGap: {
        label: 'Column Gap',
        type: 'input',
        value: {
          mobile: '10',
          tablet: '10',
          desktop: '10',
        },
      },
    }),
    ...(isGapFieldEnabled && {
      layout: {
        label: 'Layout',
        type: 'select',
        value: {
          mobile: 'row',
          tablet: 'row',
          desktop: 'row',
        },
      },
    }),
  };

  return {
    heightField: {
      type: 'custom',
      label: 'Customize Input Field',
      render: ({ value, onChange }) => {
        const [isOpen, setIsOpen] = useState(false);

        const toggleAccordion = () => {
          setIsOpen(!isOpen);
        };

        const handleChange = (key: string, option: string) => {
          onChange({
            ...value,
            [key]: {
              ...(value?.[key as keyof InputFieldHeightSubProps] as Record<
                string,
                number | string
              >),
              [value?.deviceType]: option,
            },
          });
        };

        return (
          <div>
            <div
              className="flex justify-between font-bold cursor-pointer bg-slate-100 rounded-md pagebuilder-theme"
              onClick={toggleAccordion}>
              <h3>Customize Input Field</h3>
              {!isOpen ? <KeyboardArrowDownIcon /> : <KeyboardArrowUpIcon />}
            </div>
            {isOpen && (
              <div className="flex flex-col mt-3 items-center border border-gray-300 rounded p-2 w-full">
                <div className="w-full p-2">
                  <Toggle<InputFieldHeightSubProps>
                    value={value}
                    onChange={newValue => {
                      onChange({ ...value, ...newValue });
                    }}
                    heading={{ label: 'Device', value: 'device' }}
                    options={[
                      { label: 'Desktop', value: 'desktop' },
                      { label: 'Tablet', value: 'tablet' },
                      { label: 'Mobile', value: 'mobile' },
                    ]}
                    propName="deviceType"
                  />
                </div>
                {isGapFieldEnabled && (
                  <div className="w-full p-2 ">
                    <FieldLabel
                      label={
                        (
                          <CustomTooltip title="Grid Layout">
                            <span>Layout</span>
                          </CustomTooltip>
                        ) as React.ReactNode as string
                      }>
                      <Select
                        className="pagebuilder-theme"
                        value={value?.layout?.[value?.deviceType] || 'row'}
                        onChange={e =>
                          onChange({
                            ...value,
                            layout: {
                              mobile: value?.layout?.mobile ?? 'row',
                              tablet: value?.layout?.tablet ?? 'row',
                              desktop: value?.layout?.desktop ?? 'row',
                              [value?.deviceType]: e.target.value,
                            },
                          })
                        }
                        options={[
                          {
                            label: 'Row',
                            value: 'row',
                          },
                          {
                            label: 'Column',
                            value: 'column',
                          },
                        ]}
                      />
                    </FieldLabel>
                  </div>
                )}
                {Object?.entries(inputField)?.map(([field, config]) => (
                  <div key={field} className="p-2 w-full">
                    {
                      config.type === 'input' ? (
                        <InputField
                          value={
                            (
                              value?.[
                                field as keyof InputFieldHeightSubProps
                              ] as Record<string, string>
                            )?.[value?.deviceType] || '' // Ensure proper access of value for height
                          }
                          onChange={e => {
                            let newValue = e.replace(/[^0-9%]/g, '');

                            // Ensure that only one '%' symbol remains at the end
                            if (newValue?.includes('%')) {
                              newValue =
                                newValue
                                  .replace(/%.*%/g, '%')
                                  .replace(/%/g, '') + '%';
                            }

                            // If the string is empty or contains only whitespace, set to 'auto'
                            if (!newValue?.trim()) {
                              newValue = 'auto';
                            }

                            // If the value includes both digits and '%', clean up any non-digit/% characters
                            if (/\d/.test(newValue) && /%/.test(newValue)) {
                              newValue = newValue.replace(/[^0-9%]/g, '');
                            }

                            // Default to 'auto' if the value is still empty
                            if (!newValue?.trim()) {
                              newValue = 'auto';
                            }

                            handleChange(field, newValue);
                          }}
                          field={config as CustomField<string>}
                        />
                      ) : null /* Add other conditions here if needed */
                    }
                  </div>
                ))}
              </div>
            )}
          </div>
        );
      },
      defaultItemProps: {
        height: 'h-[48px]',
      },
    },
  } as Fields<fieldHeightProps>;
};
